/**
 * Pick-your-own holiday badge (Bobbie, 2026-08-17 evening: "Can the user
 * pick the avatar the first time they sign in on that day... especially the
 * dancers, but really everybody can have the option").
 *
 * Mounted under the staff/admin poster greeting and the dancer hero, below the
 * doors on the family home (DECISIONS 112: in the hero it pushed My classes
 * down), and on /season-preview. The default
 * mode shows once per holiday per PERSON per browser: the first home visit
 * inside a live window offers the season's badge set, and picking one - or
 * "Surprise me", which keeps the dealt badge - stores the answer
 * (seasonBadgePick.ts) and the card goes away for that person. A different
 * person on the same shared device still gets their own card. `always`
 * keeps it visible with the current choice highlighted, so /season-preview
 * lets anyone change their mind mid-season.
 *
 * `changeable` is the dancer home's mode (Bobbie, 2026-08-17: "Dancers can
 * change theirs as much as they want too! That's part of the fun"): once
 * answered, instead of vanishing it collapses to a slim "Your badge: X,
 * Change" row that reopens the chips. Dancers cannot reach /season-preview
 * (their route lockdown is deliberate), so their change door lives on the
 * one page they have.
 *
 * `labels`: every display label the app renders this person by (auth
 * displayName plus the teacher-title variant for staff). The pick is stored
 * under all of them so avatars match it everywhere. The first label is the
 * person's primary name.
 *
 * The pick is device-local in v1; see seasonBadgePick.ts for why and for
 * what a cross-device version would take.
 */
import { useState } from 'react';
import { useSeason } from '../lib/season';
import {
  getSeasonBadgePick,
  setSeasonBadgePick,
  useSeasonBadgePickVersion,
} from '../lib/seasonBadgePick';
import { seasonalAvatarBadge } from '../lib/seasonalAvatarBadge';

const cardStyle: React.CSSProperties = {
  background: 'var(--paper)',
  border: '1px solid var(--season-accent-border, var(--line))',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-sm)',
  padding: 'var(--space-4)',
  marginTop: 'var(--space-4)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

/**
 * THE KIDS' SIZE (Bobbie, 2026-10-03, DECISIONS 117). She asked to "Make it
 * larger for kids as a one time selection"; asked whether that meant a
 * dancer's pick should now be final, she chose "Bigger, still changeable":
 * "Badges about 1.5 times bigger, and dancers keep the small Change row so
 * they can re-pick any time." So the dancer home passes size="large": every
 * chip and badge glyph is 1.5 times the adult size (44px chips with a 22px
 * glyph become 66px with a 33px glyph), and the card's words grow with them.
 * The folded Change row stays the regular size, as her option said. A Jr
 * Staff's email sign-in gets the same large, changeable picker as her dancer
 * sign-in (one person on either sign-in). The other adult homes and
 * /season-preview keep the regular size.
 */
const LARGE_BADGE_CHIP_PX = 66;
const LARGE_BADGE_GLYPH_PX = 33;

function chipStyle(selected: boolean, large: boolean): React.CSSProperties {
  return {
    minWidth: large ? LARGE_BADGE_CHIP_PX : 'var(--tap-target)',
    minHeight: large ? LARGE_BADGE_CHIP_PX : 'var(--tap-target)',
    padding: large ? '0 var(--space-4)' : '0 var(--space-3)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 'var(--radius-pill)',
    border: selected
      ? '2px solid var(--season-accent-border, var(--lime-border))'
      : '1px solid var(--line)',
    background: selected ? 'var(--season-accent-wash, var(--lime-whisper))' : 'var(--paper)',
    color: 'var(--ink)',
    font: 'inherit',
    fontSize: large ? LARGE_BADGE_GLYPH_PX : 22,
    fontWeight: 800,
    cursor: 'pointer',
  };
}

export function SeasonBadgePicker({
  labels,
  always = false,
  changeable = false,
  size = 'regular',
}: {
  labels: readonly string[];
  always?: boolean;
  changeable?: boolean;
  /** 'large' on the dancer homes and a Jr Staff's staff greeting (DECISIONS 117, see LARGE_BADGE_CHIP_PX). */
  size?: 'regular' | 'large';
}) {
  const { season } = useSeason();
  useSeasonBadgePickVersion();
  const [expanded, setExpanded] = useState(false);
  const large = size === 'large';
  const cleaned = labels.map((l) => l.trim()).filter(Boolean);
  if (!season || cleaned.length === 0) return null;
  const entry = getSeasonBadgePick(season.key, cleaned);
  if (entry && !always && !changeable) return null;

  // What they are wearing right now: their pick, or the dealt badge after
  // "Surprise me".
  const wearing =
    entry?.badge && season.badges.includes(entry.badge)
      ? entry.badge
      : seasonalAvatarBadge(cleaned[0], season.badges);

  if (entry && changeable && !always && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded={false}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          // The SMALL Change row at every size: her chosen option kept it.
          minHeight: 'var(--tap-target)',
          padding: '0 var(--space-4)',
          marginTop: 'var(--space-4)',
          width: '100%',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--ink)',
          font: 'inherit',
          fontSize: 14,
          fontWeight: 800,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span>
          Your {season.label} badge:{' '}
          {wearing}
        </span>
        <span style={{ color: 'var(--season-accent-text, var(--lime-deep))', flexShrink: 0 }}>
          Change
        </span>
      </button>
    );
  }

  const choose = (badge: string | null) => {
    setSeasonBadgePick(season.key, cleaned, badge);
    // In changeable mode the open card folds back to the slim row after a
    // choice, so the fun of re-picking never crowds the page for long.
    if (changeable) setExpanded(false);
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: 800, fontSize: large ? 20 : 15, color: 'var(--ink)' }}>
        {/* A no-break space keeps the glyph on the heading's last line. */}
        {entry ? `Your ${season.label} badge` : `Pick your ${season.label} badge\u00A0${season.greetingEmoji}`}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {season.badges.map((badge) => (
          <button
            key={badge}
            type="button"
            aria-pressed={entry ? entry.badge === badge : false}
            aria-label={`Wear ${badge} for ${season.label}`}
            onClick={() => choose(badge)}
            style={chipStyle(entry?.badge === badge, large)}
          >
            {badge}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={entry ? entry.badge == null : false}
          onClick={() => choose(null)}
          style={{ ...chipStyle(entry != null && entry.badge == null, large), fontSize: large ? 18 : 13 }}
        >
          Surprise me
        </button>
      </div>
      <p style={{ margin: 0, fontSize: large ? 14 : 12, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
        It rides on your avatar and greeting on this device for the season.
      </p>
    </div>
  );
}

export default SeasonBadgePicker;
